import React, { useEffect, useState } from "react";
import axios from "axios";
import { Layers, ArrowRight, Pencil, RotateCcw, Trash2, Plus, Copy, X, Save } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PageHeader, Spinner } from "../components/Brand";
import { useApp, API } from "../AppContext";

const fmt = (n) => new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD" }).format(n || 0);
const lineTotal = (it) => {
  const cost = (Number(it.material_cost || 0) + Number(it.labor_cost || 0) + Number(it.equipment_cost || 0) + Number(it.subcontractor_cost || 0)) * Number(it.quantity || 0);
  return cost * (1 + Number(it.profit_pct || 0) / 100);
};

export default function Templates() {
  const { t, lang } = useApp();
  const [items, setItems] = useState([]);
  const [ccqActive, setCcqActive] = useState(false);
  const [editing, setEditing] = useState(null); // template being edited
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    const { data } = await axios.get(`${API}/templates`);
    setItems(data);
    setCcqActive(data.length > 0 && data[0].ccq_adjusted);
  };
  useEffect(() => { load(); }, []);

  const openEditor = (tpl) => setEditing(JSON.parse(JSON.stringify(tpl)));
  const closeEditor = () => setEditing(null);

  const updateItem = (i, key, val) => {
    const next = { ...editing };
    next.items = [...next.items];
    next.items[i] = { ...next.items[i], [key]: val };
    setEditing(next);
  };
  const addItem = () => setEditing({ ...editing, items: [...editing.items, { description: "", quantity: 1, unit: "u", material_cost: 0, labor_cost: 0, equipment_cost: 0, subcontractor_cost: 0, profit_pct: 15 }] });
  const removeItem = (i) => setEditing({ ...editing, items: editing.items.filter((_, j) => j !== i) });

  const save = async () => {
    setSaving(true);
    try {
      if (editing.user_created) {
        await axios.put(`${API}/templates/${editing.id}`, {
          name: editing.name, category: editing.category, notes: editing.notes, items: editing.items,
        });
      } else if (items.find((x) => x.id === editing.id)) {
        // Existing system or already-customized
        await axios.put(`${API}/templates/${editing.id}`, {
          name: editing.name, category: editing.category, notes: editing.notes, items: editing.items,
        });
      }
      closeEditor();
      await load();
    } finally { setSaving(false); }
  };

  const reset = async (tpl) => {
    if (!confirm(t.templates_p.confirm_reset)) return;
    await axios.post(`${API}/templates/${tpl.id}/reset`);
    load();
  };

  const remove = async (tpl) => {
    if (!confirm(t.templates_p.confirm_delete)) return;
    await axios.delete(`${API}/templates/${tpl.id}`);
    load();
  };

  const duplicate = async (tpl) => {
    const { data } = await axios.post(`${API}/templates`, {
      name: `${tpl.name} ${lang === "fr" ? "(copie)" : "(copy)"}`,
      category: tpl.category, notes: tpl.notes || "", items: tpl.items,
    });
    if (data?.id) {
      load();
    }
  };

  const createNew = () => {
    setEditing({
      id: `tpl-custom-${Date.now()}`,
      name: t.templates_p.new,
      category: t.templates_p.custom,
      notes: "",
      items: [],
      user_created: true,
      is_custom: true,
    });
  };

  return (
    <div>
      <PageHeader
        title={t.templates_p.title}
        subtitle={ccqActive ? (lang === "fr" ? "Tarifs ajustés CCQ +5% (en vigueur depuis le 26 avril 2026)" : "Rates adjusted CCQ +5% (effective April 26, 2026)") : null}
        action={<button className="btn-red text-sm" onClick={createNew} data-testid="new-template-btn"><Plus size={14} /> {t.templates_p.new}</button>}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((tpl) => (
          <div key={tpl.id} className="glass-card p-6 group" data-testid={`tpl-${tpl.id}`}>
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.25)" }}>
                <Layers size={18} color="#D4AF37" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-heading text-base font-semibold" style={{ color: "#F5F5F5" }}>{tpl.name}</h3>
                  {tpl.is_custom && (
                    <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full"
                          style={{ background: "rgba(192,0,0,0.15)", color: "#FF6B6B", border: "1px solid rgba(192,0,0,0.3)" }}>
                      {t.templates_p.custom}
                    </span>
                  )}
                </div>
                <p className="text-xs" style={{ color: "#D4AF37" }}>{tpl.category}</p>
              </div>
            </div>
            <p className="text-xs mb-2" style={{ color: "rgba(245,245,245,0.55)" }}>
              {tpl.items.length} item(s)
            </p>
            {tpl.notes && (
              <p className="text-[10px] uppercase tracking-wider mb-4" style={{ color: "#D4AF37" }}>{tpl.notes}</p>
            )}

            <div className="flex flex-wrap gap-2 mt-4">
              <button className="btn-ghost text-xs py-2 px-3 flex-1 justify-center min-w-[100px]" onClick={() => openEditor(tpl)} data-testid={`edit-${tpl.id}`}>
                <Pencil size={12} color="#D4AF37" /> {t.templates_p.edit}
              </button>
              <button className="btn-ghost text-xs py-2 px-3" onClick={() => duplicate(tpl)} data-testid={`dup-${tpl.id}`} title={t.templates_p.duplicate}>
                <Copy size={12} />
              </button>
              {tpl.is_custom && !tpl.user_created && (
                <button className="btn-ghost text-xs py-2 px-3" onClick={() => reset(tpl)} data-testid={`reset-${tpl.id}`} title={t.templates_p.reset}>
                  <RotateCcw size={12} color="#D4AF37" />
                </button>
              )}
              {tpl.user_created && (
                <button className="btn-ghost text-xs py-2 px-3" onClick={() => remove(tpl)} data-testid={`del-${tpl.id}`} title={t.templates_p.delete}>
                  <Trash2 size={12} color="#C00000" />
                </button>
              )}
              <button className="btn-red text-xs py-2 px-3 w-full justify-center mt-1" onClick={() => nav("/app/quotes/new")} data-testid={`use-${tpl.id}`}>
                {t.templates_p.use} <ArrowRight size={12} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Editor Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4" style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)" }} onClick={closeEditor}>
          <div className="glass-strong w-full max-w-5xl my-8 p-6 sm:p-8" onClick={(e) => e.stopPropagation()} data-testid="editor-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-bold" style={{ color: "#F5F5F5" }}>{t.templates_p.edit}: {editing.name}</h2>
              <button onClick={closeEditor} className="p-2 rounded-full hover:bg-white/5" data-testid="editor-close">
                <X size={20} color="#F5F5F5" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
              <div>
                <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{t.templates_p.tpl_name}</label>
                <input className="input-dark mt-2" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="editor-name" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{t.templates_p.tpl_category}</label>
                <input className="input-dark mt-2" value={editing.category || ""} onChange={(e) => setEditing({ ...editing, category: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>{t.templates_p.tpl_notes}</label>
                <input className="input-dark mt-2" value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
              </div>
            </div>

            <div className="overflow-x-auto mb-4">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.5)" }}>
                    <th className="py-2 pr-2">{lang === "fr" ? "Description" : "Description"}</th>
                    <th className="pr-2">{lang === "fr" ? "Qté" : "Qty"}</th>
                    <th className="pr-2">{lang === "fr" ? "Unité" : "Unit"}</th>
                    <th className="pr-2">{lang === "fr" ? "Matériel" : "Material"}</th>
                    <th className="pr-2">{lang === "fr" ? "M.O." : "Labor"}</th>
                    <th className="pr-2">{lang === "fr" ? "Équip." : "Equip."}</th>
                    <th className="pr-2">{lang === "fr" ? "S.-tr." : "Sub."}</th>
                    <th className="pr-2">Profit %</th>
                    <th className="pr-2">Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {editing.items.map((it, i) => (
                    <tr key={i} className="border-t" style={{ borderColor: "rgba(245,245,245,0.05)" }}>
                      <td className="py-2 pr-2 min-w-[200px]"><input className="input-dark" style={{ padding: "0.4rem 0.6rem" }} value={it.description || ""} onChange={(e) => updateItem(i, "description", e.target.value)} data-testid={`tpl-desc-${i}`} /></td>
                      <td className="pr-2"><input type="number" step="0.01" className="input-dark w-20" style={{ padding: "0.4rem 0.6rem" }} value={it.quantity ?? 0} onChange={(e) => updateItem(i, "quantity", parseFloat(e.target.value) || 0)} /></td>
                      <td className="pr-2"><input className="input-dark w-20" style={{ padding: "0.4rem 0.6rem" }} value={it.unit || ""} onChange={(e) => updateItem(i, "unit", e.target.value)} /></td>
                      <td className="pr-2"><input type="number" step="0.01" className="input-dark w-20" style={{ padding: "0.4rem 0.6rem" }} value={it.material_cost ?? 0} onChange={(e) => updateItem(i, "material_cost", parseFloat(e.target.value) || 0)} /></td>
                      <td className="pr-2"><input type="number" step="0.01" className="input-dark w-20" style={{ padding: "0.4rem 0.6rem" }} value={it.labor_cost ?? 0} onChange={(e) => updateItem(i, "labor_cost", parseFloat(e.target.value) || 0)} /></td>
                      <td className="pr-2"><input type="number" step="0.01" className="input-dark w-20" style={{ padding: "0.4rem 0.6rem" }} value={it.equipment_cost ?? 0} onChange={(e) => updateItem(i, "equipment_cost", parseFloat(e.target.value) || 0)} /></td>
                      <td className="pr-2"><input type="number" step="0.01" className="input-dark w-20" style={{ padding: "0.4rem 0.6rem" }} value={it.subcontractor_cost ?? 0} onChange={(e) => updateItem(i, "subcontractor_cost", parseFloat(e.target.value) || 0)} /></td>
                      <td className="pr-2"><input type="number" step="0.1" className="input-dark w-16" style={{ padding: "0.4rem 0.6rem" }} value={it.profit_pct ?? 0} onChange={(e) => updateItem(i, "profit_pct", parseFloat(e.target.value) || 0)} /></td>
                      <td className="font-semibold whitespace-nowrap pr-2" style={{ color: "#D4AF37" }}>{fmt(lineTotal(it))}</td>
                      <td>
                        <button onClick={() => removeItem(i)} className="p-1.5 rounded-full hover:bg-red-500/10" data-testid={`del-item-${i}`}>
                          <Trash2 size={12} color="#C00000" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <button onClick={addItem} className="btn-ghost text-xs" data-testid="editor-add-item">
              <Plus size={12} color="#D4AF37" /> {t.templates_p.add_item}
            </button>

            <div className="flex justify-end gap-2 mt-6 pt-6 border-t" style={{ borderColor: "rgba(212,175,55,0.15)" }}>
              <button onClick={closeEditor} className="btn-ghost text-sm">{t.templates_p.cancel}</button>
              <button onClick={save} disabled={saving} className="btn-red text-sm" data-testid="editor-save">
                {saving ? <><Spinner /> {t.templates_p.save}</> : <><Save size={14} /> {t.templates_p.save}</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
